import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import { searchOrganizations } from "@/api/one_c_pickers";
import * as api from "@/api/system_settings";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toastError } from "@/lib/api-errors";

export function OrgWhitelistCard({
  title,
  description,
  field,
  current,
  searchFn = searchOrganizations,
  queryKey = "oc-all-orgs",
  entityLabel = "организации",
  hint,
  emptyLabel = "все",
}: {
  title: string;
  /** Пояснение под заголовком: у белого списка смысл не всегда очевиден из
   *  названия — «склады-фулфилменты» сами по себе не говорят, что меняется. */
  description?: string;
  field:
    | "order_check_org_refs"
    | "transfer_check_org_refs"
    | "logistics_org_refs"
    | "receiving_org_refs"
    | "inventory_org_refs"
    | "dashboard_hidden_organization_refs"
    | "inventory_warehouse_refs"
    | "stock_transfer_org_refs"
    | "stock_transfer_warehouse_refs"
    | "transfer_receipt_warehouse_refs"
    | "fulfilment_warehouse_refs";
  current: string[];
  searchFn?: (q: string) => Promise<{ ref: string; name: string }[]>;
  queryKey?: string;
  entityLabel?: string;
  /** Текст над списком. По умолчанию — смысл БЕЛОГО списка («ничего не
   *  отмечено — не применяется»); у списка-исключения он обратный. */
  hint?: string;
  /** Что значит пустой выбор: у белого списка «все», у исключений — «никто». */
  emptyLabel?: string;
}): React.ReactElement {
  const qc = useQueryClient();
  const orgsQ = useQuery({
    queryKey: [queryKey],
    queryFn: () => searchFn(""),
    staleTime: 5 * 60_000,
  });
  const [sel, setSel] = useState<Set<string>>(new Set(current));
  const [q, setQ] = useState("");
  useEffect(() => setSel(new Set(current)), [current]);

  const saveMut = useMutation({
    mutationFn: (refs: string[]) => api.updateSystemSettings({ [field]: refs }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["system-settings"] });
      toast.success("Сохранено");
    },
    onError: toastError("Ошибка сохранения"),
  });

  const orgs = (orgsQ.data ?? []).filter(
    (o) => !q || o.name.toLowerCase().includes(q.toLowerCase()),
  );
  function toggle(ref: string) {
    setSel((prev) => {
      const n = new Set(prev);
      if (n.has(ref)) n.delete(ref);
      else n.add(ref);
      return n;
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {hint ??
            `Отметьте ${entityLabel} для этого списка. Если ничего не отмечено — список не применяется.`}
        </p>
        <Input
          placeholder={`Поиск (${entityLabel})`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {orgsQ.isLoading && (
          <p className="text-sm text-muted-foreground">Загрузка ({entityLabel})…</p>
        )}
        {orgsQ.isError && (
          <p className="text-sm text-destructive">
            Не удалось загрузить {entityLabel} из 1С (нужно рабочее подключение к
            1С у администратора).
          </p>
        )}
        {!orgsQ.isLoading && !orgsQ.isError && (
          <div className="max-h-80 space-y-1 overflow-auto rounded border p-2">
            {orgs.map((o) => (
              <label key={o.ref} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={sel.has(o.ref)}
                  onChange={() => toggle(o.ref)}
                  className="size-4"
                />
                {o.name}
              </label>
            ))}
            {orgs.length === 0 && (
              <p className="text-sm text-muted-foreground">Ничего не найдено</p>
            )}
          </div>
        )}
        <div className="flex items-center gap-2">
          <Button onClick={() => saveMut.mutate([...sel])} disabled={saveMut.isPending}>
            Сохранить
          </Button>
          {sel.size > 0 && (
            <Button variant="ghost" onClick={() => setSel(new Set())}>
              Сбросить ({emptyLabel})
            </Button>
          )}
          <span className="text-xs text-muted-foreground">
            Выбрано: {sel.size || emptyLabel}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
